import React, { useState, useRef, useCallback, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import LockIcon from "@mui/icons-material/Lock";

const basePath =
  process.env.NODE_ENV === "production" ? "/edge" : "http://localhost:3010";

const CODE_LENGTH = 6;

export default function TwoFactorVerify({ twoFactorToken, onSuccess, onCancel }) {
  const [code, setCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const codeInputRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => codeInputRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, [useBackupCode]);

  const submitCode = useCallback(
    async (value) => {
      setLoading(true);
      setError(null);

      try {
        const res = await fetch(`${basePath}/auth/2fa/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ twoFactorToken, code: value }),
          credentials: "include",
          mode: "cors",
        });

        const data = await res.json();

        if (!res.ok || !data?.user) {
          setError(data?.message || "Verification failed");
          setLoading(false);
          return;
        }

        onSuccess(data.user);
      } catch {
        setError("An error occurred. Please try again.");
        setLoading(false);
      }
    },
    [twoFactorToken, onSuccess]
  );

  const handleCodeChange = (e) => {
    const cleaned = e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH);
    setCode(cleaned);
    if (cleaned.length === CODE_LENGTH) {
      submitCode(cleaned);
    }
  };

  const handleBackupSubmit = (e) => {
    e.preventDefault();
    if (backupCode.trim()) {
      submitCode(backupCode.trim());
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.85)",
        zIndex: 9999,
      }}
    >
      <Paper
        sx={{
          p: 4,
          maxWidth: 420,
          width: "100%",
          mx: 2,
          textAlign: "center",
        }}
      >
        <LockIcon sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />

        <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
          Two-Factor Authentication
        </Typography>

        <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
          {useBackupCode
            ? "Enter one of your backup recovery codes"
            : "Enter the 6-digit code from your authenticator app"}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>
            {error}
          </Alert>
        )}

        {!useBackupCode ? (
          <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
            <input
              ref={codeInputRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={CODE_LENGTH}
              value={code}
              onChange={handleCodeChange}
              disabled={loading}
              placeholder="------"
              style={{
                textAlign: "center",
                fontSize: "2rem",
                fontWeight: 700,
                fontFamily: "monospace",
                letterSpacing: "0.6em",
                width: 240,
                padding: "14px 16px",
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.14)",
                backgroundColor: "rgba(255,255,255,0.03)",
                color: "#fff",
                outline: "none",
                caretColor: "#E8A33D",
              }}
            />
          </Box>
        ) : (
          <form onSubmit={handleBackupSubmit}>
            <TextField
              fullWidth
              inputRef={codeInputRef}
              value={backupCode}
              onChange={(e) => setBackupCode(e.target.value)}
              disabled={loading}
              placeholder="e.g. a1b2c3d4"
              variant="outlined"
              sx={{ mb: 3 }}
            />
            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={loading || !backupCode.trim()}
              sx={{ mb: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : "Verify Backup Code"}
            </Button>
          </form>
        )}

        {loading && !useBackupCode && (
          <CircularProgress size={28} sx={{ mb: 2 }} />
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Button
            size="small"
            onClick={() => {
              setUseBackupCode(!useBackupCode);
              setError(null);
              setCode("");
              setBackupCode("");
            }}
            disabled={loading}
          >
            {useBackupCode
              ? "Use authenticator code instead"
              : "Use a backup code"}
          </Button>

          <Button size="small" onClick={onCancel} disabled={loading} color="inherit">
            Cancel
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
